import type { DtcCodeEntity } from '@packages/domain/dtc-codes';

import { colors } from '@packages/core/styles';
import { Icon } from '@packages/ui/shared';
import { useCallback } from 'react';
import { AsyncTypeahead, Token } from 'react-bootstrap-typeahead';
import { createUseStyles } from 'react-jss';

/**
 * Props for the {@link SearchDtcCode} component.
 */
type SearchDtcCodeProps = {
    /** Whether a search request is currently in flight. */
    isLoading: boolean;

    /** The list of DTC code search results to display in the dropdown. */
    options: DtcCodeEntity[];

    /** The currently selected DTC codes (shown as tokens). */
    selected: DtcCodeEntity[];

    /** Callback fired when the user types a search query. */
    onSearch: (query: string) => void;

    /** Callback fired when the user selects a DTC code from the dropdown. */
    onChange: (option: DtcCodeEntity[]) => void;

    /** Callback fired when the user removes a selected DTC code token. */
    onRemove: (option: DtcCodeEntity) => void;
};

/** Props passed by react-bootstrap-typeahead to the renderToken callback's second argument. */
type TokenProps = {
    /** The tab index for the token element. */
    tabIndex?: number;
};

/**
 * Renders an async typeahead search input for DTC codes. Selected codes appear as tokens with primary/non-primary
 * styling. Each token can be removed, and dropdown items show the code and description.
 *
 * @param props - the component props
 * @returns the DTC code search typeahead
 */
export function SearchDtcCode({ isLoading, onChange, onRemove, onSearch, options, selected }: SearchDtcCodeProps) {
    const classes = useStyles();

    // Handlers

    /** Generates the display label for a DTC code option. */
    const handleLabelKey = useCallback((option: DtcCodeEntity) => `${option.code} - ${option.description ?? ''}`, []);

    /** Renders a dropdown menu item for a DTC code search result. */
    const handleRenderMenuItemChildren = useCallback(
        (item: DtcCodeEntity) => (
            <>
                <p className={classes.menuItem}>
                    {item.code}
                    {item.description && <span>{` - ${item.description}`}</span>}
                </p>
            </>
        ),
        [classes.menuItem]
    );

    /** Renders a selected DTC code token with primary/non-primary styling. */
    const handleRenderToken = useCallback(
        (option: DtcCodeEntity, { tabIndex }: TokenProps, index: number) => (
            <Token
                className={`${classes.token} ${option?.isPrimary ? classes.tokenBlue : classes.tokenGray}`}
                key={`${option.code}-${index}`}
                // eslint-disable-next-line react/jsx-no-bind -- option is scoped to the renderToken callback parameter; a closure is unavoidable here
                onRemove={() => onRemove(option)}
                option={option}
                tabIndex={tabIndex}
            >
                <span className={classes.tokenOptionLabelContainer}>
                    {option?.isPrimary && <Icon color="blueOne" name="star-filled" size={12} />}
                    {option.code}
                </span>
            </Token>
        ),
        [classes.token, classes.tokenBlue, classes.tokenGray, classes.tokenOptionLabelContainer, onRemove]
    );

    return (
        <AsyncTypeahead<DtcCodeEntity>
            className={classes.asyncTypeahead}
            id="dtc-codes-search"
            isLoading={isLoading}
            labelKey={handleLabelKey}
            multiple={true}
            onChange={onChange}
            onSearch={onSearch}
            options={options}
            placeholder="Search DTC codes"
            renderMenuItemChildren={handleRenderMenuItemChildren}
            renderToken={handleRenderToken}
            selected={selected}
            useCache={false}
        />
    );
}

const useStyles = createUseStyles({
    asyncTypeahead: {
        '& .rbt-input': {
            borderRadius: '4px !important',
        },
        '& .rbt-input-main': {
            fontSize: '15px !important',
            fontWeight: '400 !important',
        },
        '& .rbt-input-wrapper': {
            display: 'flex',
            alignItems: 'center !important',
            borderRadius: '4px !important',
            '& button.close': {},
        },
        '& .rbt-aux': {
            display: 'none',
        },
        '&& $token': {
            borderRadius: '4px !important',
        },
        '&& $tokenBlue': {
            backgroundColor: `${colors.blueFour} !important`,
            color: `${colors.blueOne} !important`,
            border: `1px solid ${colors.blueTwo} !important`,
        },
        '&& $tokenGray': {
            backgroundColor: `${colors.grayTwo} !important`,
            color: `${colors.textDarkSecondary} !important`,
            border: `1px solid ${colors.grayThree} !important`,
        },
    },
    token: {
        height: `20px !important`,
        paddingTop: '12px !important',
        paddingBottom: '12px !important',
        paddingRight: '8px !important',
        display: 'inline-flex !important',
        alignItems: 'center !important',
        borderRadius: '4px !important',
        fontWeight: 700,
        '& .rbt-token-remove-button': {
            marginLeft: '4px !important',
            display: 'inline-flex !important',
            alignItems: 'center !important',
            fontSize: '22px',
        },
    },
    tokenBlue: {
        paddingLeft: '6px !important',
    },
    tokenGray: {
        '& .rbt-token-remove-button': {
            backgroundColor: `${colors.grayTwo} !important`,
            '& span': {
                color: `${colors.textDarkSecondary} !important`,
                backgroundColor: `${colors.grayTwo} !important`,
            },
        },
    },
    menuItem: {
        marginBottom: 0,
        fontSize: '15px',
    },
    tokenOptionLabelContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
});
